import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useDesignSystem } from "../../lib/design/designSystem";
import SearchIcon from "../icons/SearchIcon";
import { foldSearchText } from "../../utils/searchFold";
import { getOptionTitle } from "./selectOptionText";

interface GlassSelectWithSearchProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "size" | "multiple"
  > {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
  size?: "xs" | "sm" | "md" | "lg";
  placeholder?: string;
  searchPlaceholder?: string;
  /**
   * Minimum width (px) for the portal dropdown menu. Defaults to the trigger's
   * width. Set higher when the trigger is compact but option labels are long
   * (e.g. a narrow header league selector with full league names) so options
   * aren't truncated in the list.
   */
  menuMinWidth?: number;
  /**
   * Let option labels wrap onto multiple lines instead of being clipped by
   * `truncate`. Use for rich two-line options (e.g. stadium name + location).
   */
  wrapOptionLabels?: boolean;
}

type GlassSelectOption = {
  value: string;
  label: React.ReactNode;
  disabled: boolean;
  searchText?: string;
  /** Full text of the option (`title` attribute, else its text) for tooltips. */
  title?: string;
  /**
   * Compact label shown in the trigger instead of `label` when the option
   * carries `data-trigger-label` (e.g. « LRF Annaba » for the full league name).
   */
  triggerLabel?: string;
  /**
   * Section header row (`<option disabled data-section-header>`): never
   * selectable nor highlighted, but styled as a heading, not as a dimmed
   * disabled option.
   */
  isSectionHeader?: boolean;
};

const optionHeightMap: Record<
  NonNullable<GlassSelectWithSearchProps["size"]>,
  number
> = {
  xs: 32,
  sm: 36,
  md: 40,
  lg: 48,
};

const mergeRefs = <T,>(...refs: Array<React.Ref<T> | undefined>) => {
  return (instance: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(instance);
      } else {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - mutable ref assignment
          ref.current = instance;
        } catch {
          // Ignore assignment errors
        }
      }
    });
  };
};

const GlassSelectWithSearch = React.forwardRef<
  HTMLSelectElement,
  GlassSelectWithSearchProps
>(
  (
    {
      label,
      error,
      helperText,
      containerClassName,
      size = "md",
      className,
      disabled,
      children,
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      id,
      searchPlaceholder,
      menuMinWidth,
      wrapOptionLabels = false,
      ...restProps
    },
    ref
  ) => {
    const { t } = useTranslation("common");
    const { surfaces, typography } = useDesignSystem();
    const resolvedSearchPlaceholder = searchPlaceholder ?? t("pagination.search_placeholder");
    const generatedId = useId();
    const selectId = id ?? `${generatedId}-select`;
    const labelId = label ? `${selectId}-label` : undefined;
    const listboxIdValue = `${selectId}-listbox`;
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{
      top: number;
      left: number;
      width: number;
    } | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchTerm, setSearchTerm] = useState("");

    const options = useMemo<GlassSelectOption[]>(() => {
      const collected: GlassSelectOption[] = [];

      const collect = (nodes: React.ReactNode) => {
        React.Children.forEach(nodes, (child) => {
          if (!React.isValidElement(child)) return;

          const element = child as React.ReactElement<{
            children?: React.ReactNode;
            value?: unknown;
            disabled?: boolean;
            title?: string;
            "data-search-text"?: string;
            "data-trigger-label"?: string;
            "data-section-header"?: boolean | string;
          }>;
          const { type, props } = element;

          if (type === React.Fragment) {
            collect(props.children);
            return;
          }

          if (typeof type === "string" && type === "optgroup") {
            collect(props.children);
            return;
          }

          if (typeof type === "string" && type === "option") {
            const optionValue =
              props.value ??
              (typeof props.children === "string" ? props.children : "");
            collected.push({
              value: optionValue != null ? String(optionValue) : "",
              label: props.children,
              disabled: Boolean(props.disabled),
              searchText: props["data-search-text"],
              title: getOptionTitle(props.title, props.children),
              triggerLabel: props["data-trigger-label"] || undefined,
              isSectionHeader:
                Boolean(props["data-section-header"]) &&
                props["data-section-header"] !== "false",
            });
          }
        });
      };

      collect(children);
      return collected;
    }, [children]);

    const isControlled = value !== undefined;

    const [uncontrolledValue, setUncontrolledValue] = useState<string>(() => {
      if (value !== undefined && value !== null) {
        return Array.isArray(value) ? value[0] ?? "" : String(value);
      }
      if (defaultValue !== undefined && defaultValue !== null) {
        return Array.isArray(defaultValue)
          ? defaultValue[0] ?? ""
          : String(defaultValue);
      }
      if (placeholder !== undefined) return "";
      return options[0]?.value ?? "";
    });

    useEffect(() => {
      if (isControlled) return;
      if (defaultValue !== undefined && defaultValue !== null) {
        setUncontrolledValue(
          Array.isArray(defaultValue)
            ? defaultValue[0] ?? ""
            : String(defaultValue)
        );
      }
    }, [defaultValue, isControlled]);

    useEffect(() => {
      if (isControlled) return;
      if (!options.some((option) => option.value === uncontrolledValue)) {
        setUncontrolledValue(options[0]?.value ?? "");
      }
    }, [isControlled, options, uncontrolledValue]);

    const currentValueRaw = isControlled ? value : uncontrolledValue;
    const normalizedCurrentValue = Array.isArray(currentValueRaw)
      ? currentValueRaw[0] ?? ""
      : currentValueRaw != null
      ? String(currentValueRaw)
      : "";

    useEffect(() => {
      if (!selectRef.current) return;
      selectRef.current.value = normalizedCurrentValue;
    }, [normalizedCurrentValue]);

    const filteredOptions = useMemo(() => {
      if (!searchTerm.trim()) return options;
      const normalizedSearch = foldSearchText(searchTerm);
      return options.filter((option) => {
        // A ReactNode label stringifies to "[object Object]", so a rich option
        // MUST carry `data-search-text` to stay searchable.
        const haystack =
          option.searchText ??
          (typeof option.label === "string"
            ? option.label
            : String(option.label ?? ""));
        return foldSearchText(haystack).includes(normalizedSearch);
      });
    }, [options, searchTerm]);

    const selectedOption = useMemo(
      () =>
        filteredOptions.find(
          (option) => option.value === normalizedCurrentValue
        ) ?? null,
      [filteredOptions, normalizedCurrentValue]
    );

    const optionHeight = optionHeightMap[size] ?? optionHeightMap.md;

    const computeMenuPosition = useCallback(() => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const rowHeight = wrapOptionLabels ? optionHeight + 18 : optionHeight;
      const estimatedHeight =
        Math.min(Math.max(filteredOptions.length, 1), 6) * rowHeight + 56; // space for search
      let top = rect.bottom + 8;
      if (top + estimatedHeight > viewportHeight - 8) {
        top = Math.max(8, rect.top - estimatedHeight);
      }
      // Menu is at least as wide as the trigger, optionally wider (menuMinWidth)
      // so long option labels aren't truncated, but never wider than the viewport.
      const width = Math.min(
        viewportWidth - 16,
        Math.max(rect.width, menuMinWidth ?? 0)
      );
      let left = rect.left;
      if (left + width > viewportWidth - 8) {
        left = Math.max(8, viewportWidth - width - 8);
      }
      setMenuPosition({ top, left, width });
    }, [filteredOptions.length, optionHeight, menuMinWidth, wrapOptionLabels]);

    const closeMenu = useCallback((focusButton = false) => {
      setIsOpen(false);
      setMenuPosition(null);
      setSearchTerm("");
      if (focusButton) {
        requestAnimationFrame(() => {
          buttonRef.current?.focus();
        });
      }
    }, []);

    const dispatchFocusEvent = useCallback((type: "focus" | "blur") => {
      const element = selectRef.current;
      if (!element) return;
      const fallback = new Event(type, { bubbles: type === "focus" });
      try {
        const event = new FocusEvent(type, { bubbles: type === "focus" });
        element.dispatchEvent(event);
      } catch {
        element.dispatchEvent(fallback);
      }
    }, []);

    const handleNativeChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!isControlled) {
          setUncontrolledValue(event.target.value);
        }
        onChange?.(event);
      },
      [isControlled, onChange]
    );

    const getNextEnabledIndex = useCallback(
      (startIndex: number, direction: 1 | -1) => {
        if (!filteredOptions.length) return -1;
        for (let offset = 1; offset <= filteredOptions.length; offset += 1) {
          const index =
            (startIndex + direction * offset + filteredOptions.length) %
            filteredOptions.length;
          if (!filteredOptions[index]?.disabled) {
            return index;
          }
        }
        return filteredOptions.findIndex((option) => !option.disabled);
      },
      [filteredOptions]
    );

    useEffect(() => {
      if (!filteredOptions.length) {
        setHighlightedIndex(-1);
        return;
      }

      const currentIndex = filteredOptions.findIndex(
        (option) => option.value === normalizedCurrentValue
      );
      if (currentIndex >= 0 && !filteredOptions[currentIndex]?.disabled) {
        setHighlightedIndex(currentIndex);
        return;
      }

      const nextIndex = getNextEnabledIndex(-1, 1);
      setHighlightedIndex(nextIndex >= 0 ? nextIndex : -1);
    }, [filteredOptions, getNextEnabledIndex, normalizedCurrentValue]);

    const handleOptionSelect = useCallback(
      (option: GlassSelectOption) => {
        if (disabled || option.disabled) return;
        if (!selectRef.current) return;
        selectRef.current.value = option.value;
        selectRef.current.dispatchEvent(new Event("change", { bubbles: true }));
        closeMenu(true);
      },
      [closeMenu, disabled]
    );

    const openMenu = useCallback(() => {
      if (disabled || !options.length) return;
      computeMenuPosition();
      setIsOpen(true);
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }, [computeMenuPosition, disabled, options.length]);

    useEffect(() => {
      if (!isOpen) return;
      computeMenuPosition();

      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current?.contains(event.target as Node) ||
          buttonRef.current?.contains(event.target as Node)
        ) {
          return;
        }
        closeMenu(false);
      };

      const handleWindowScroll = () => computeMenuPosition();

      window.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", handleWindowScroll);
      window.addEventListener("scroll", handleWindowScroll, true);

      return () => {
        window.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("resize", handleWindowScroll);
        window.removeEventListener("scroll", handleWindowScroll, true);
      };
    }, [closeMenu, computeMenuPosition, isOpen]);

    useEffect(() => {
      if (!isOpen || !menuRef.current || highlightedIndex < 0) return;
      const activeItem = menuRef.current.querySelector<HTMLElement>(
        `[data-option-index="${highlightedIndex}"]`
      );
      activeItem?.scrollIntoView({ block: "nearest" });
    }, [highlightedIndex, isOpen]);

    const sizeClasses = {
      xs: "px-2 py-1 text-xs",
      sm: "px-2.5 py-1.5 text-xs",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-2.5 text-base",
    };

    const showPlaceholder =
      placeholder !== undefined &&
      (normalizedCurrentValue === "" || !selectedOption);

    const displayNode = showPlaceholder ? (
      <span className="text-white/40">{placeholder}</span>
    ) : (
      selectedOption?.triggerLabel ?? selectedOption?.label ?? (
        <span className="text-white/40">{placeholder ?? t("actions.select")}</span>
      )
    );

    const triggerTitle = showPlaceholder
      ? placeholder
      : selectedOption?.title ?? selectedOption?.searchText;

    const buttonClasses = clsx(
      "flex w-full items-center justify-between gap-2 rounded-lg border border-white/10",
      "bg-white/5 text-start text-white backdrop-blur transition-all duration-200",
      !disabled && "cursor-pointer hover:border-white/20 hover:bg-white/10",
      "focus-visible:border-primary-500/50 focus-visible:bg-white/10 focus-visible:outline-none",
      sizeClasses[size],
      disabled && surfaces.inputDisabled,
      error && "border-rose-500/50 focus-visible:border-rose-500/70",
      className
    );

    const handleButtonClick = () => {
      if (disabled || !options.length) return;
      if (isOpen) {
        closeMenu(false);
      } else {
        openMenu();
      }
    };

    const handleButtonKeyDown = (
      event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
      if (disabled) return;

      const moveHighlight = (direction: 1 | -1) => {
        if (!filteredOptions.length) return;
        const nextIndex = getNextEnabledIndex(
          highlightedIndex >= 0
            ? highlightedIndex
            : direction === 1
            ? -1
            : filteredOptions.length,
          direction
        );
        if (nextIndex >= 0) {
          setHighlightedIndex(nextIndex);
        }
      };

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          const currentIndex = filteredOptions.findIndex(
            (option) => option.value === normalizedCurrentValue
          );
          const nextIndex = getNextEnabledIndex(
            currentIndex >= 0 ? currentIndex : -1,
            1
          );
          if (nextIndex >= 0) {
            setHighlightedIndex(nextIndex);
          }
        } else {
          moveHighlight(1);
        }
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          const currentIndex = filteredOptions.findIndex(
            (option) => option.value === normalizedCurrentValue
          );
          const nextIndex = getNextEnabledIndex(
            currentIndex >= 0 ? currentIndex : filteredOptions.length,
            -1
          );
          if (nextIndex >= 0) {
            setHighlightedIndex(nextIndex);
          }
        } else {
          moveHighlight(-1);
        }
        return;
      }

      if (event.key === "Home") {
        if (!isOpen) return;
        event.preventDefault();
        const firstIndex = getNextEnabledIndex(-1, 1);
        if (firstIndex >= 0) {
          setHighlightedIndex(firstIndex);
        }
        return;
      }

      if (event.key === "End") {
        if (!isOpen) return;
        event.preventDefault();
        const lastIndex = getNextEnabledIndex(filteredOptions.length, -1);
        if (lastIndex >= 0) {
          setHighlightedIndex(lastIndex);
        }
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (isOpen) {
          const option =
            highlightedIndex >= 0
              ? filteredOptions[highlightedIndex]
              : undefined;
          if (option && !option.disabled) {
            handleOptionSelect(option);
          }
        } else {
          openMenu();
        }
        return;
      }

      if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        closeMenu(true);
      }
    };

    const handleButtonFocus = () => dispatchFocusEvent("focus");
    const handleButtonBlur = () => dispatchFocusEvent("blur");

    return (
      <div className={clsx("space-y-1", containerClassName)}>
        {label && (
          <label id={labelId} htmlFor={selectId} className={typography.label}>
            {label}
          </label>
        )}

        <select
          id={selectId}
          ref={mergeRefs(selectRef, ref)}
          value={normalizedCurrentValue}
          onChange={handleNativeChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
          className="sr-only"
          {...restProps}
          aria-labelledby={labelId}
        >
          {children}
        </select>

        <button
          ref={buttonRef}
          type="button"
          className={buttonClasses}
          onClick={handleButtonClick}
          onKeyDown={handleButtonKeyDown}
          onFocus={handleButtonFocus}
          onBlur={handleButtonBlur}
          aria-haspopup="listbox"
          aria-controls={listboxIdValue}
          aria-expanded={isOpen}
          aria-disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-label={labelId ? undefined : restProps["aria-label"]}
          aria-labelledby={labelId}
        >
          <span className="flex-1 truncate text-start" title={triggerTitle}>
            {displayNode}
          </span>
          <span className="flex items-center text-white/60">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.138l3.71-3.907a.75.75 0 111.08 1.04l-4.25 4.475a.75.75 0 01-1.08 0L5.25 8.29a.75.75 0 01-.02-1.08z" />
            </svg>
          </span>
        </button>

        {isOpen && menuPosition
          ? createPortal(
              <div
                ref={menuRef}
                id={listboxIdValue}
                role="listbox"
                aria-activedescendant={
                  highlightedIndex >= 0
                    ? `${listboxIdValue}-option-${highlightedIndex}`
                    : undefined
                }
                className="fixed z-[10000] max-h-80 overflow-hidden rounded-lg border border-white/15 bg-white/10 backdrop-blur-xl backdrop-saturate-150 shadow-xl shadow-black/40"
                style={{
                  top: menuPosition.top,
                  left: menuPosition.left,
                  width: menuPosition.width,
                }}
                aria-labelledby={labelId}
              >
                <div className="p-2 border-b border-white/10 bg-black/10">
                  <div className="relative">
                    <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      onKeyDown={(event) => {
                        // The menu is portaled outside the trigger, so the trigger's
                        // Escape handler never sees this; without it Escape reaches
                        // window unhandled and closes the enclosing drawer/modal.
                        if (event.key === "Escape") {
                          event.preventDefault();
                          closeMenu(true);
                        }
                      }}
                      placeholder={resolvedSearchPlaceholder}
                      className="w-full bg-white/5 border border-white/10 rounded-md py-2 ps-9 pe-3 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary-500/70"
                    />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto px-1 py-1">
                  {filteredOptions.length ? (
                    filteredOptions.map((option, index) => {
                      const isSelected =
                        option.value === normalizedCurrentValue;
                      const isHighlighted = index === highlightedIndex;
                      const optionClasses = clsx(
                        "flex w-full justify-between gap-2 rounded-md px-2.5 py-2 text-sm transition-colors",
                        wrapOptionLabels ? "items-start" : "items-center",
                        option.isSectionHeader
                          ? "cursor-default pt-2.5 pb-1 text-white/60"
                          : option.disabled
                          ? "cursor-not-allowed text-white/30"
                          : isHighlighted
                          ? "bg-white/20 text-white"
                          : "text-white/90 hover:bg-white/15",
                        isSelected &&
                          !option.disabled &&
                          "font-semibold text-white"
                      );

                      return (
                        <button
                          key={`${option.value}-${index}`}
                          type="button"
                          role={option.isSectionHeader ? "presentation" : "option"}
                          aria-selected={option.isSectionHeader ? undefined : isSelected}
                          disabled={option.disabled}
                          className={optionClasses}
                          data-option-index={index}
                          id={`${listboxIdValue}-option-${index}`}
                          tabIndex={-1}
                          onMouseEnter={() => {
                            if (!option.disabled) {
                              setHighlightedIndex(index);
                            }
                          }}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleOptionSelect(option)}
                          title={option.title}
                        >
                          <span
                            className={clsx(
                              "min-w-0 flex-1 text-start",
                              !wrapOptionLabels && "truncate"
                            )}
                          >
                            {option.label}
                          </span>
                          {isSelected && (
                            <span className="text-primary-200">✓</span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-2.5 py-3 text-center text-sm text-white/60">
                      {t("pagination.no_results_short")}
                    </div>
                  )}
                </div>
              </div>,
              document.body
            )
          : null}

        {error && <p className="text-xs text-rose-300">{error}</p>}
        {helperText && !error && (
          <p className={clsx(typography.caption)}>{helperText}</p>
        )}
      </div>
    );
  }
);

GlassSelectWithSearch.displayName = "GlassSelectWithSearch";

export default GlassSelectWithSearch;
